import {DiscountType, Plan} from '@rechargeapps/storefront-client';
import {ProductState, SellingPlanGroup} from './types';

const discountTypes: DiscountType[] = [
  'percentage',
  'fixed_amount',
  'shipping',
];

function isDiscountType(value: string): value is DiscountType {
  return discountTypes.includes(value as DiscountType);
}

const intervalUnits = ['day', 'week', 'month'] as const;

function isIntervalUnit(
  value: string
): value is (typeof intervalUnits)[number] {
  return intervalUnits.includes(value as (typeof intervalUnits)[number]);
}

export default function createOptimisticSellingPlan(
  sellingPlanGroup: SellingPlanGroup,
  product: ProductState
): Plan | null {
  const sellingPlan = sellingPlanGroup.selling_plans[0];

  if (!sellingPlan) return null;

  const rechargePlanId = sellingPlan.options.find(
    (option) => option.name === 'Recharge Plan ID'
  )?.value;

  const orderFrequencyAndUnit = sellingPlan.options.find(
    (option) => option.name === 'Order Frequency and Unit'
  )?.value;
  const [frequency, unit] = orderFrequencyAndUnit?.split('-') ?? [];

  const discountType = sellingPlan.price_adjustments[0].value_type;

  return {
    id: rechargePlanId ? parseInt(rechargePlanId) : 0,
    channel_settings: {
      api: {
        display: true,
      },
      checkout_page: {
        display: true,
      },
      customer_portal: {
        display: true,
      },
      merchant_portal: {
        display: true,
      },
    },
    created_at: new Date().toISOString(),
    deleted_at: null,
    discount_amount: sellingPlan.price_adjustments[0].value.toString(),
    discount_type: isDiscountType(discountType)
      ? discountType
      : discountTypes[0],
    external_plan_group_id: sellingPlanGroup.app_id,
    external_plan_id: sellingPlan.id,
    external_plan_name: sellingPlan.name,
    external_product_id: {
      ecommerce: product.id,
    },
    external_variant_ids: product.variants.map((variant) => variant.id),
    has_variant_restrictions: false,
    sort_order: 0,
    subscription_preferences: {
      apply_cutoff_date_to_checkout: false,
      charge_interval_frequency: frequency ? parseInt(frequency) : null,
      cutoff_day_of_month: null,
      cutoff_day_of_week: null,
      expire_after_specific_number_of_charges: null,
      interval_unit: unit && isIntervalUnit(unit) ? unit : null,
      order_day_of_month: null,
      order_day_of_week: null,
      order_interval_frequency: frequency ? parseInt(frequency) : null,
      pricing_progression: [],
    },
    title: sellingPlan.name ?? '',
    type: 'subscription',
    updated_at: new Date().toISOString(),
  };
}
